import { makeAutoObservable, runInAction } from "mobx";

import type { BranchesInBrand } from "@/api/admin/brands";
import type { IBrandGetById } from "@/api/admin/brands";
import { brandsApi } from "@/api/admin/brands/brandApi";
import {
  getCuisineIdsFromBrandProperties,
  mergeBrandPropertiesWithCuisine,
} from "@/api/admin/brands/brandPropertyUtils";
import {
  getMenuCacheEntry,
  setMenuQueryData,
} from "@/api/admin/brands/menuQueryUtils";
import { groupsApi } from "@/api/admin/groups";
import type {
  BrandContactsType,
  BrandFormDetailsType,
  BrandFormMenuType,
} from "@/components/units";
import {
  getDefaultFormMenu,
  mapFormToMenuPost,
  mapMenuGetToForm,
} from "@/components/units/BrandFormMenu/menuMappers";
import type { Branch } from "@/store/BranchStore";
import type { BrandFormPayload, BrandFullPayload } from "@/store/BrandStore";
import { rootStore } from "@/store/RootStore";

export class DemoBrandStore {
  details: BrandFormDetailsType;
  menu: BrandFormMenuType;
  contacts: BrandContactsType;
  branches: Branch[] = [];
  branchesInBrand: BranchesInBrand[] = [];
  active: boolean = false;

  // Loading states
  isCreating = false;
  isUpdating = false;
  isFetching = false;

  get isNew(): boolean {
    return !this.details?.id;
  }

  constructor(
    groupId?: string,
    brandId?: string,
    readonly hasFixedGroupId: boolean = false
  ) {
    makeAutoObservable(this);
    this.details = this.getDefaultDetails(groupId);
    this.menu = this.getDefaultMenu();
    this.contacts = this.getDefaultContacts();
    this.branches = [];
    this.active = false;
    if (brandId) {
      this.fetchBrand(brandId);
    } else {
      this.prepareGroup(groupId);
    }
  }

  private async prepareGroup(groupId?: string) {
    if (!groupId) {
      return;
    }

    try {
      const group = await groupsApi.getGroupById(groupId);
      runInAction(() => {
        this.details = {
          ...(this.details ?? this.getDefaultDetails()),
          country: group.country,
          currency: group.currency,
        };
      });
    } catch (error) {
      console.error("DemoBrandStore: error preparing group", error);
    }
  }

  setDetails(details: BrandFormDetailsType) {
    this.details = details;
  }

  setMenu(menu: BrandFormMenuType) {
    this.menu = menu;
    rootStore.menuStore.initMenu(menu);
  }

  setContacts(contacts: BrandContactsType) {
    this.contacts = contacts;
  }

  setBranches(branches: Branch[]) {
    this.branches = branches;
  }

  setAll(data: BrandFormPayload) {
    if (data.details) {
      this.details = {
        ...(this.details ?? this.getDefaultDetails()),
        ...data.details,
      };
    }
    if (data.menu) {
      const currentMenu = this.menu ?? this.getDefaultMenu();
      const menuWithBranches: BrandFormMenuType = {
        ...currentMenu,
        ...data.menu,
        categories: (data.menu.categories ?? currentMenu.categories ?? []).map(
          category => ({
            ...category,
            dishes: category.dishes.map(dish => ({
              ...dish,
              unavailableBranchIds: dish.unavailableBranchIds ?? [],
            })),
          })
        ),
      };
      this.menu = menuWithBranches;
      rootStore.menuStore.initMenu(this.menu);
    }
    if (data.contacts) {
      this.contacts = {
        ...(this.contacts ?? this.getDefaultContacts()),
        ...data.contacts,
      };
    }
    if (data.branches) {
      this.branches = data.branches;
    }
    if (data.active !== undefined) {
      this.active = data.active;
    }
  }

  getBrand(): BrandFullPayload {
    return {
      details: this.details ?? this.getDefaultDetails(),
      menu: this.menu ?? this.getDefaultMenu(),
      contacts: this.contacts ?? this.getDefaultContacts(),
      branches: this.branches,
      active: this.active,
    };
  }

  getFullPayload(): BrandFullPayload {
    return this.getBrand();
  }

  getSnapshot(): { brandId?: string; groupId?: string } {
    return {
      brandId: this.details?.id,
      groupId: this.details?.groupId,
    };
  }

  getDefaultDetails(groupId?: string): BrandFormDetailsType {
    return {
      googleBusinessId: "",
      descriptionEn: "",
      descriptionAr: "",
      country: "",
      currency: "",
      nameEn: "",
      nameAr: "",
      groupId: groupId || "",
      legalEntityId: "",
      mediaItemId: "",
      logoUrl: null,
    };
  }

  getDefaultMenu(): BrandFormMenuType {
    return getDefaultFormMenu();
  }

  getDefaultContacts(): BrandContactsType {
    return {
      email: "",
      websiteUrl: null,
      socialLinks: [{ type: "", url: "" }],
      connectedAccounts: [],
    };
  }

  /**
   * Fetch brand by ID, update store, and sync branches into shared menuStore.
   */
  async fetchBrand(brandId: string): Promise<IBrandGetById> {
    this.isFetching = true;

    try {
      const [data, menuData] = await Promise.all([
        brandsApi.getBrandById(brandId),
        brandsApi.getMenu(brandId),
      ]);

      setMenuQueryData(brandId, menuData);

      runInAction(() => {
        const {
          branches,
          VAT,
          commercialRegistration,
          country,
          currency,
          descriptionAr,
          descriptionEn,
          googleBusinessId,
          groupId,
          legalEntityId,
          id,
          nameAr,
          nameEn,
          referenceNumber,
          email,
          phone,
          socialLinks,
          websiteUrl,
          active,
          logoUrl,
          mediaItemId,
          groupCountry,
          groupCurrency,
        } = data;

        this.details = {
          VAT,
          commercialRegistration,
          country,
          currency,
          descriptionAr,
          descriptionEn,
          googleBusinessId,
          groupId,
          id,
          nameAr,
          nameEn,
          referenceNumber,
          legalEntityId,
          logoUrl,
          mediaItemId,
          groupCountry,
          groupCurrency,
        };

        this.active = !!active;
        const cuisineIds = getCuisineIdsFromBrandProperties(data.properties);
        this.menu = menuData
          ? { ...mapMenuGetToForm(menuData), cuisine: cuisineIds }
          : { ...this.getDefaultMenu(), cuisine: cuisineIds };
        this.branches = [];
        this.branchesInBrand = branches;

        this.contacts = {
          email,
          phone,
          socialLinks,
          websiteUrl,
        };
        this.isFetching = false;

        rootStore.menuStore.initMenu(this.menu);
        rootStore.menuStore.setBranches(
          branches.map(b => ({
            branchId: b.branchId,
            branchName: b.branchName,
            city: b.cityName,
          }))
        );
      });
      return data;
    } catch (error) {
      runInAction(() => {
        this.isFetching = false;
      });
      throw error;
    }
  }

  async upsertMenu(isActive = false): Promise<void> {
    if (!this.details?.id) return;

    const brandId = this.details.id;
    const menuPayload = {
      ...mapFormToMenuPost(this.menu),
      isActive,
    };

    const { isLoaded, menu: cachedMenu } = getMenuCacheEntry(brandId);
    const menu = isLoaded
      ? cachedMenu
        ? await brandsApi.patchMenu(brandId, menuPayload)
        : await brandsApi.createMenu(brandId, menuPayload)
      : await (async () => {
          const existing = await brandsApi.getMenu(brandId);
          setMenuQueryData(brandId, existing);
          return existing
            ? await brandsApi.patchMenu(brandId, menuPayload)
            : await brandsApi.createMenu(brandId, menuPayload);
        })();

    setMenuQueryData(brandId, menu);

    const brandData = await brandsApi.getBrandById(brandId);
    const properties = mergeBrandPropertiesWithCuisine(
      brandData.properties,
      this.menu.cuisine ?? []
    );
    await brandsApi.patchBrand(brandId, { properties });

    runInAction(() => {
      this.menu = {
        ...mapMenuGetToForm(menu),
        cuisine: this.menu.cuisine ?? [],
      };
      rootStore.menuStore.initMenu(this.menu);
    });
  }

  prepareData() {
    const storePayload = this.getFullPayload();

    const socialLinks =
      storePayload.contacts?.socialLinks
        ?.filter(link => {
          const type = link.type?.trim() ?? "";
          const url = link.url?.trim() ?? "";

          return type || url;
        })
        .map(link => ({
          ...link,
          type: link.type?.trim() ?? "",
          url: link.url?.trim() ?? "",
        })) ?? [];

    const payload = {
      ...storePayload.details,
      websiteUrl: storePayload.contacts?.websiteUrl?.trim() || null,
      email: storePayload.contacts?.email?.trim() || null,
      active: storePayload.active,
      socialLinks,
    };

    return payload;
  }

  async upsertBrand(): Promise<IBrandGetById> {
    if (!this.details?.id) {
      return this.createBrand();
    }
    return this.updateBrand();
  }

  /**
   * Create brand and update store
   */
  async createBrand(skipRefetch = false): Promise<IBrandGetById> {
    this.isCreating = true;
    try {
      const payload = this.prepareData();
      const created = await brandsApi.postNewBrand(payload);
      runInAction(() => {
        this.isCreating = false;
      });

      if (skipRefetch) {
        return created;
      }

      // Fetch the created brand to get full data including branches
      return await this.fetchBrand(created.id!);
    } catch (error) {
      runInAction(() => {
        this.isCreating = false;
      });
      throw error;
    }
  }

  async updateBrand(): Promise<IBrandGetById> {
    if (!this.details?.id) {
      throw new Error("Cannot update brand: no brand ID");
    }
    this.isUpdating = true;
    try {
      const payload = this.prepareData();
      await brandsApi.patchBrand(this.details.id, payload);
      const refreshed = await this.fetchBrand(this.details.id);
      runInAction(() => {
        this.isUpdating = false;
      });
      return refreshed;
    } catch (error) {
      runInAction(() => {
        this.isUpdating = false;
      });
      throw error;
    }
  }
}
